import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Stack,
  LinearProgress,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material'
import {
  Shield,
  Refresh,
  WarningAmber,
  ErrorOutline,
  CheckCircle,
  ManageSearch,
  PersonOff,
} from '@mui/icons-material'
import axios from 'axios'
import API from '../../../../services/API'
import FailedChecks from './FailedChecks'
import { TenantSettingsContext } from '../../../../App'

const getCardColour = (color) => {
  if (color === 'grey') {
    return {
      bg: 'grey.100',
      icon: 'grey.700',
      text: 'grey.700',
    }
  }

  return {
    bg: `${color}.light`,
    icon: `${color}.dark`,
    text: `${color}.main`,
  }
}

export default function DBSDashboard() {
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [data, setData] = useState({
    total: 0,
    current: 0,
    notBlank: 0,
    newInfo: 0,
    noMatch: 0,
    failed: 0,
    notOnUpdateService: 0,
    dbsCheckExpiry: 36,
    lastRunAt: null,
    checks: [],
    monitoredWithoutNumber: [],
    unmonitoredChecks: [],
    staffWithoutDBS: [],
  })

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const dbsCompanySettings =
    orgSettings?.staffGovernanceSettings?.dbsUpdateServiceSettings
  const checksDisabled =
    !dbsCompanySettings?.enabled ||
    !dbsCompanySettings?.organisationName ||
    !dbsCompanySettings?.declarationAcceptedAt ||
    !dbsCompanySettings?.declarationAcceptedBy

  useEffect(() => {
    fetchDashboard()
  }, [])

  const fetchDashboard = async () => {
    setLoading(true)
    await API.get('/dbs-dashboard')
      .then((res) => {
        if (res.status === 200) {
          const rows = res.data.summary || []

          const counts = rows.reduce((acc, row) => {
            acc[row._id || 'NOT_CHECKED'] = row.count
            return acc
          }, {})

          const current =
            (counts.BLANK_NO_NEW_INFO || 0) +
            (counts.NON_BLANK_NO_NEW_INFO || 0)

          const total = rows.reduce((sum, row) => sum + row.count, 0)

          const unableToCheck =
            (counts.CHECK_FAILED || 0) +
            (counts.INVALID_REQUEST || 0) +
            (counts.MISSING_REQUIRED_DATA || 0) +
            (counts.NO_MATCH_FOUND || 0)

          setData((prev) => ({
            ...prev,
            total,
            current,
            newInfo: counts.NEW_INFO || 0,
            notBlank: counts.NON_BLANK_NO_NEW_INFO || 0,
            failed: unableToCheck,
            notOnUpdateService: res.data.notOnUpdateService || 0,
            dbsCheckExpiry: res.data.dbsCheckExpiry ?? 36,
            checks: res.data.checks || [],
            monitoredWithoutNumber: res.data.monitoredWithoutNumber || [],
            unmonitoredChecks: res.data.unmonitoredChecks || [],
            staffWithoutDBS: res.data.staffWithoutDBS || [],
            lastRunAt: res.data.lastRunAt || null,
          }))
        }
      })
      .catch((e) => console.error(e.message))
      .finally(() => setLoading(false))
  }

  const runChecksNow = async () => {
    setRunning(true)
    await API.post('/dbs/run-checks-now')
    await fetchDashboard()
    setRunning(false)
  }

  // Monitored records with no certificate number can never be checked, so
  // they count as monitored-but-not-clear rather than dropping out of view.
  const withoutNumberCount = data.monitoredWithoutNumber.length
  const monitoredTotal = data.total + withoutNumberCount

  const completionRate = useMemo(() => {
    if (!monitoredTotal) return 0
    return Math.round((data.current / monitoredTotal) * 100)
  }, [data, monitoredTotal])

  const withoutDBSCount = data.staffWithoutDBS.length
  const unmonitoredAttentionCount =
    data.unmonitoredChecks.length + withoutDBSCount

  const cards = [
    {
      label: 'Current',
      value: data.current,
      icon: <CheckCircle />,
      color: 'success',
      helper: (
        <>
          DBS checks current on Update Service.{' '}
          {data.notBlank ? (
            <>
              This includes <strong>{data.notBlank}</strong> with info on the
              original certificate.
            </>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      label: 'New Information',
      value: data.newInfo,
      icon: <WarningAmber />,
      color: 'error',
      helper: 'Requires new DBS certificate',
    },
    {
      label: 'Unable to Check',
      value: data.failed + withoutNumberCount,
      icon: <ErrorOutline />,
      color: 'warning',
      helper: (
        <>
          Certificate details could not be checked or matched.{' '}
          {withoutNumberCount ? (
            <>
              <strong>{withoutNumberCount}</strong>{' '}
              {withoutNumberCount === 1 ? 'has' : 'have'} no certificate number.
            </>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      // Everyone the Update Service is not watching: manual certificates and
      // staff with no DBS recorded at all.
      label: 'Not on Update Service',
      value: data.notOnUpdateService + withoutDBSCount,
      icon: <PersonOff />,
      color: unmonitoredAttentionCount ? 'warning' : 'grey',
      helper: (
        <>
          <strong>{data.notOnUpdateService}</strong> manual DBS{' '}
          {data.notOnUpdateService === 1 ? 'record' : 'records'} and{' '}
          <strong>{withoutDBSCount}</strong> staff with no DBS recorded.{' '}
          {unmonitoredAttentionCount ? (
            <>
              <strong>{unmonitoredAttentionCount}</strong>{' '}
              {unmonitoredAttentionCount === 1 ? 'needs' : 'need'} attention.
            </>
          ) : (
            ''
          )}
        </>
      ),
    },
  ]

  // One worklist for the whole DBS position: Update Service results needing
  // action, manual certificates that have lapsed or were never completed,
  // and staff with no DBS recorded at all.
  const attentionItems = [
    ...data.checks.map((check) => {
      const result = check.lastUpdateServiceCheck
      return {
        key: check._id,
        userId: check.userId,
        employeeName: check.employeeName,
        caption: check.reference
          ? `Ref: ${check.reference}`
          : 'No certificate number',
        type: 'Update Service',
        chip: <StatusChip status={result?.status} />,
        note: result?.detail,
        details: result?.date
          ? `Last checked ${new Date(result.date).toLocaleString()}`
          : 'Not checked',
        action: getRecommendedAction(result?.status),
      }
    }),
    ...data.monitoredWithoutNumber.map((check) => ({
      key: check._id,
      userId: check.userId,
      employeeName: check.employeeName,
      caption: 'No certificate number',
      type: 'Update Service',
      chip: <Chip label="No certificate number" color="warning" size="small" />,
      details: check.completed
        ? `Certificate dated ${new Date(check.completed).toLocaleDateString()}`
        : check.status
          ? `Record status: ${check.status}`
          : 'Never checked',
      action:
        "Add the 12-digit certificate number to the employee's DBS record so it can be checked, or switch monitoring off if they are not subscribed.",
    })),
    ...data.unmonitoredChecks.map((check) => ({
      key: check._id,
      userId: check.userId,
      employeeName: check.employeeName,
      caption: check.reference
        ? `Ref: ${check.reference}`
        : 'No certificate reference',
      type: 'Manual record',
      chip: <UnmonitoredStatusChip check={check} />,
      details: check.completed
        ? `Certificate dated ${new Date(check.completed).toLocaleDateString()}`
        : 'No completed certificate',
      action: check.completed
        ? 'Request a new DBS certificate, or subscribe the employee to the Update Service.'
        : 'Complete the DBS check and record the certificate reference and date.',
    })),
    ...data.staffWithoutDBS.map((person) => ({
      key: `none-${person.employeeId}`,
      userId: person.userId,
      employeeName: person.employeeName,
      caption: person.clinicalRole || 'No role recorded',
      type: 'No DBS',
      chip: <Chip label="No DBS recorded" color="error" size="small" />,
      details:
        person.status === 'New Starter' ? 'New starter' : 'Active employee',
      action:
        "Request a DBS certificate and add it to the employee's DBS record.",
    })),
  ]

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="title2">DBS Monitoring</Typography>
          <Typography color="text.secondary">
            DBS compliance for all active staff: Update Service results, manual
            certificates and staff with no DBS recorded.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={runChecksNow}
          disabled={running || checksDisabled}
        >
          {checksDisabled
            ? 'Update Checks Disabled'
            : running
              ? 'Checking...'
              : 'Run Checks Now'}
        </Button>
      </Stack>

      {running && (
        <Alert severity="info" sx={{ mb: 3 }}>
          DBS checks are running. Results will update when complete.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {cards.map((card) => {
          const colours = getCardColour(card.color)

          return (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Card
                sx={{
                  borderRadius: 3,
                  height: '100%',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: colours.bg,
                        color: colours.icon,
                      }}
                    >
                      {card.icon}
                    </Box>

                    <Box>
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ color: colours.text }}
                      >
                        {card.value}
                      </Typography>
                      <Typography fontWeight={600}>{card.label}</Typography>
                    </Box>
                  </Stack>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    {card.helper}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Shield />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700}>Update Service Coverage</Typography>
              <Typography variant="body2" color="text.secondary">
                {completionRate}% of monitored DBS checks are currently clear.
              </Typography>
            </Box>
            <Chip label={`${data.current}/${monitoredTotal}`} color="success" />
          </Stack>

          <LinearProgress
            variant="determinate"
            value={completionRate}
            sx={{ height: 10, borderRadius: 5 }}
          />

          {data.lastRunAt && (
            <Typography variant="caption" color="text.secondary">
              Last checked: {new Date(data.lastRunAt).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Items Requiring Attention
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update Service results needing action, monitored records with no
            certificate number, manual certificates older than{' '}
            {data.dbsCheckExpiry} months or not completed, and staff with no DBS
            recorded.
          </Typography>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Issue</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>Recommended Action</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {attentionItems.map((item) => (
                <TableRow key={item.key} hover>
                  <TableCell>
                    <Typography fontWeight={600}>
                      {item.employeeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.caption}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {item.type}
                  </TableCell>

                  <TableCell>
                    {item.chip}
                    {item.note && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5, maxWidth: 320 }}
                      >
                        {item.note}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>{item.details}</TableCell>

                  <TableCell>{item.action}</TableCell>

                  <TableCell align="right">
                    {item.userId && (
                      <Tooltip title="Open employee record">
                        <IconButton href={`/employees/${item.userId}`}>
                          <ManageSearch />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {!loading && attentionItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="success">
                      No DBS checks currently require attention.
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <FailedChecks failedCount={data.failed} />
    </Box>
  )
}

function UnmonitoredStatusChip({ check }) {
  if (check.completed) {
    return <Chip label="Certificate expired" color="error" size="small" />
  }

  return (
    <Chip
      label={check.status || 'Not completed'}
      color="warning"
      size="small"
    />
  )
}

function StatusChip({ status }) {
  switch (status) {
    case 'BLANK_NO_NEW_INFO':
      return <Chip label="Current - Blank" color="success" size="small" />

    case 'NON_BLANK_NO_NEW_INFO':
      return <Chip label="Current - Has Info" color="info" size="small" />

    case 'NEW_INFO':
      return <Chip label="New Information" color="error" size="small" />

    case 'NO_MATCH_FOUND':
      return <Chip label="No Match Found" color="warning" size="small" />

    case 'CHECK_FAILED':
      return <Chip label="Check Failed" color="default" size="small" />

    default:
      return <Chip label="Unknown" size="small" />
  }
}

function getRecommendedAction(status) {
  switch (status) {
    case 'NEW_INFO':
      return 'Request a new DBS certificate and review suitability.'

    case 'NO_MATCH_FOUND':
      return 'Check certificate number, surname, date of birth, or subscription.'

    case 'CHECK_FAILED':
      return 'Retry the check or review service availability.'

    case 'NON_BLANK_NO_NEW_INFO':
      return 'Confirm existing information is known and risk assessed.'

    default:
      return 'No action required.'
  }
}
